// src/components/staff/mobile/MobileStaffAlerts.jsx
// Mobile version of AlertWidget.js — visible to Supervisor (75+), Manager (90+), Admin (100)
// Send alerts to clients via Email / SMS / Telegram + history

import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Send, Mail, MessageCircle, Phone, Bell, CheckCircle, Clock, Users, History, X, Eye, EyeOff, Zap } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const GLOBAL_STYLE = `@keyframes spin { to { transform: rotate(360deg) } }`;

const authFetch = (url, opts = {}) => {
  const token = localStorage.getItem('access_token');
  return fetch(url, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers ?? {}) } });
};

const ALERT_TYPES = [
  { id: 'emergency', label: 'Emergency', color: '#ef4444', bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.35)' },
  { id: 'warning',   label: 'Warning',   color: '#eab308', bg: 'rgba(234,179,8,0.15)', border: 'rgba(234,179,8,0.3)' },
  { id: 'info',      label: 'Info',      color: '#3b82f6', bg: 'rgba(59,130,246,0.15)', border: 'rgba(59,130,246,0.3)' },
  { id: 'success',   label: 'Success',   color: '#22c55e', bg: 'rgba(34,197,94,0.15)',  border: 'rgba(34,197,94,0.3)' },
];

const AUDIENCES = [
  { id: 'all',    label: 'All clients' },
  { id: 'hangar', label: 'Hangar tenants' },
  { id: 'custom', label: 'Custom' },
];

const inputStyle = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 12, padding: '12px 14px', color: '#f1f5f9', fontSize: 15,
  outline: 'none', boxSizing: 'border-box',
  fontFamily: '-apple-system,BlinkMacSystemFont,sans-serif',
};

const AlertTypeBtn = ({ type, active, onSelect }) => (
  <button onClick={() => onSelect(type.id)} style={{
    flex: 1, padding: '10px 4px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: active ? 700 : 400,
    background: active ? type.bg : '#1e293b',
    color: active ? type.color : '#64748b',
    outline: active ? `1px solid ${type.border}` : '0.5px solid #334155',
    transition: 'all 0.15s',
  }}>
    {type.label}
  </button>
);

// ─── Channel Toggle ───────────────────────────────────────────────────────────
const ChannelToggle = ({ icon: Icon, iconColor, label, checked, onChange, disabled, status }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: disabled ? '#0f172a' : '#1e293b', borderRadius: 14, border: `0.5px solid ${disabled ? '#1e293b' : '#334155'}`, opacity: disabled ? 0.45 : 1 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ width: 32, height: 32, borderRadius: 10, background: `${iconColor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={15} color={iconColor} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#f1f5f9' }}>{label}</div>
        {status && <div style={{ fontSize: 10, color: status.configured ? '#22c55e' : '#64748b', marginTop: 1 }}>{status.configured ? 'Connected' : 'Not configured'}</div>}
      </div>
    </div>
    <div onClick={disabled ? undefined : () => onChange(!checked)} style={{ position: 'relative', width: 44, height: 26, cursor: disabled ? 'default' : 'pointer' }}>
      <div style={{ width: 44, height: 26, borderRadius: 13, background: checked ? '#22c55e' : '#334155', transition: 'background 0.2s', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 2, left: checked ? 20 : 2, width: 22, height: 22, borderRadius: 11, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 0.2s' }} />
      </div>
    </div>
  </div>
);

// ─── History Item ─────────────────────────────────────────────────────────────
const HistoryItem = ({ alert }) => {
  const type = ALERT_TYPES.find(t => t.id === alert.priority) ?? ALERT_TYPES[1];
  return (
    <div style={{ background: type.bg, border: `0.5px solid ${type.border}`, borderRadius: 14, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <AlertTriangle size={14} color={type.color} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>{alert.title}</span>
        </div>
        <span style={{ fontSize: 10, color: '#64748b', flexShrink: 0 }}>
          {new Date(alert.sent_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{alert.message}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Users size={11} color="#64748b" />
          <span style={{ fontSize: 11, color: '#64748b' }}>{alert.recipients_count ?? 0} recipients</span>
        </div>
        {(alert.channels_used ?? []).map((ch, i) => (
          <span key={i} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: 'rgba(255,255,255,0.07)', color: '#94a3b8' }}>{ch}</span>
        ))}
        {alert.success && <CheckCircle size={12} color="#22c55e" />}
      </div>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const MobileStaffAlerts = () => {
  const { hasMinLevel } = useAuth();
  const canSend = hasMinLevel(75);

  const [view, setView] = useState('compose'); // 'compose' | 'history'
  const [alertType, setAlertType] = useState('warning');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState('all');
  const [customEmails, setCustomEmails] = useState('');
  const [channels, setChannels] = useState({ email: true, telegram: false, sms: false });
  const [channelsStatus, setChannelsStatus] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    authFetch(`${API_BASE}/alerts/channels/status`)
      .then(r => r.json()).then(setChannelsStatus).catch(() => {});
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/alerts/history`);
      const data = await res.json();
      setHistory(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => { if (view === 'history') loadHistory(); }, [view, loadHistory]);

  const handleSend = async () => {
    if (!title.trim()) { alert('Please enter a title'); return; }
    if (!message.trim()) { alert('Please enter a message'); return; }
    const selected = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
    if (selected.length === 0) { alert('Select at least one channel'); return; }

    setSending(true);
    try {
      const payload = { title, message, priority: alertType, channels: selected };
      if (audience === 'all') payload.send_to_all_owners = true;
      else if (audience === 'hangar') payload.send_to_hangar_tenants = true;
      else if (audience === 'custom' && customEmails) {
        payload.email_recipients = customEmails.split(',').map(e => e.trim()).filter(Boolean);
      }

      const res = await authFetch(`${API_BASE}/alerts/send`, { method: 'POST', body: JSON.stringify(payload) });
      const data = await res.json();

      setLastSent({ title, priority: alertType, success: res.ok, sent_at: new Date().toISOString() });
      setTitle('');
      setMessage('');
      setCustomEmails('');
      setTimeout(() => setLastSent(null), 5000);
    } catch (err) {
      alert('Failed to send alert: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const currentType = ALERT_TYPES.find(t => t.id === alertType) ?? ALERT_TYPES[1];

  return (
    <div style={{ padding: '0 16px 100px' }}>
      <style>{GLOBAL_STYLE}</style>

      {/* View switcher */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[{ id: 'compose', label: 'Send Alert' }, { id: 'history', label: 'History' }].map(v => (
          <button key={v.id} onClick={() => setView(v.id)} style={{
            flex: 1, padding: '10px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 13,
            fontWeight: view === v.id ? 700 : 400,
            background: view === v.id ? (v.id === 'compose' ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.15)') : '#1e293b',
            color: view === v.id ? (v.id === 'compose' ? '#ef4444' : '#3b82f6') : '#64748b',
            outline: view === v.id ? `1px solid ${v.id === 'compose' ? 'rgba(239,68,68,0.3)' : 'rgba(59,130,246,0.3)'}` : '0.5px solid #334155',
          }}>{v.label}</button>
        ))}
      </div>

      {/* Success banner */}
      {lastSent && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', background: 'rgba(34,197,94,0.12)', border: '0.5px solid rgba(34,197,94,0.3)', borderRadius: 14, marginBottom: 14 }}>
          <CheckCircle size={15} color="#22c55e" />
          <span style={{ fontSize: 13, color: '#86efac', fontWeight: 600 }}>Alert sent successfully</span>
        </div>
      )}

      {view === 'compose' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Alert type */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 8 }}>Alert type</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {ALERT_TYPES.map(t => <AlertTypeBtn key={t.id} type={t} active={alertType === t.id} onSelect={setAlertType} />)}
            </div>
          </div>

          {/* Title */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>Title *</div>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} placeholder="e.g. Runway closure — NOTAMed" />
          </div>

          {/* Message */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>Message *</div>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} placeholder="Alert details…" style={{ ...inputStyle, resize: 'none', lineHeight: 1.5 }} />
            <div style={{ fontSize: 11, color: '#334155', marginTop: 4, textAlign: 'right' }}>{message.length}/500</div>
          </div>

          {/* Audience */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 8 }}>Send to</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {AUDIENCES.map(a => (
                <button key={a.id} onClick={() => setAudience(a.id)} style={{
                  flex: 1, padding: '10px 4px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 11,
                  fontWeight: audience === a.id ? 700 : 400,
                  background: audience === a.id ? 'rgba(59,130,246,0.15)' : '#1e293b',
                  color: audience === a.id ? '#3b82f6' : '#64748b',
                  outline: audience === a.id ? '1px solid rgba(59,130,246,0.3)' : '0.5px solid #334155',
                }}>{a.label}</button>
              ))}
            </div>
            {audience === 'custom' && (
              <div style={{ marginTop: 10 }}>
                <input type="text" value={customEmails} onChange={e => setCustomEmails(e.target.value)} style={inputStyle} placeholder="email1@example.com, email2@example.com" />
                <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>Separate multiple emails with commas</div>
              </div>
            )}
          </div>

          {/* Channels */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 8 }}>Notification channels</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <ChannelToggle icon={Mail} iconColor="#3b82f6" label="Email" checked={channels.email} onChange={v => setChannels({ ...channels, email: v })} status={{ configured: true }} />
              <ChannelToggle icon={MessageCircle} iconColor="#38bdf8" label="Telegram" checked={channels.telegram} onChange={v => setChannels({ ...channels, telegram: v })} disabled={!channelsStatus?.telegram?.configured} status={channelsStatus?.telegram} />
              <ChannelToggle icon={Phone} iconColor="#22c55e" label="SMS" checked={channels.sms} onChange={v => setChannels({ ...channels, sms: v })} disabled={!channelsStatus?.sms?.configured} status={channelsStatus?.sms} />
            </div>
          </div>

          {/* Preview toggle */}
          <button onClick={() => setShowPreview(p => !p)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#3b82f6', fontSize: 13, padding: 0 }}>
            {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
            {showPreview ? 'Hide preview' : 'Show preview'}
          </button>

          {/* Preview */}
          {showPreview && (
            <div style={{ background: currentType.bg, border: `0.5px solid ${currentType.border}`, borderRadius: 16, padding: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
                <AlertTriangle size={15} color={currentType.color} />
                <span style={{ fontSize: 12, fontWeight: 700, color: currentType.color }}>{alertType.toUpperCase()}</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9', marginBottom: 6 }}>{title || '[Alert title]'}</div>
              <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.5 }}>{message || '[Alert message]'}</div>
              <div style={{ fontSize: 11, color: '#475569', marginTop: 8 }}>Sent from aeroTrick FBO</div>
            </div>
          )}

          {/* Send button */}
          <button
            onClick={handleSend}
            disabled={sending || !title.trim() || !message.trim()}
            style={{
              width: '100%', padding: '16px', borderRadius: 16,
              background: sending || !title.trim() || !message.trim() ? '#1e293b' : '#dc2626',
              border: 'none', color: sending || !title.trim() || !message.trim() ? '#475569' : '#fff',
              fontSize: 16, fontWeight: 700, cursor: sending ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'all 0.2s',
            }}
          >
            {sending
              ? <><div style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> Sending…</>
              : <><Zap size={17} /> Send Alert</>
            }
          </button>

        </div>
      ) : (
        /* History */
        <div>
          {historyLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
              <div style={{ width: 28, height: 28, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            </div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155' }}>
              <Bell size={36} color="#334155" style={{ marginBottom: 12 }} />
              <p style={{ color: '#475569' }}>No alerts sent yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {history.map((a, i) => <HistoryItem key={a.id ?? i} alert={a} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileStaffAlerts;